import React, { useState } from 'react';
import { Dialog, DialogContent, TextField, Button, ToggleButton, ToggleButtonGroup, Box, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const apiUrl = 'http://wholesale.firehemp.com:5000';

// Create a custom theme with yellow primary and gray background
const customTheme = createTheme({
  palette: {
    primary: {
      main: '#FFD700',  // Yellow color for buttons, inputs, etc.
    },
    background: {
      default: '#3c3c3c',  // Gray background
    },
    text: {
      primary: '#FFD700',  // Yellow text color
    },
  },
  components: {
    MuiFilledInput: {
      input: {
        "&::placeholder": {
          color: "#FFD700"
        },
        color: "#FFD700",
      }
    },
    MuiInput: {
      input: {
        "&::placeholder": {
          color: "#FFD700"
        },
        color: "#FFD700",
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: '#FFD700',
          '&::placeholder': {
            color: '#FFD700',
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFD700',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFD700',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFD700',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#FFD700',
          '&.Mui-focused': {
            color: '#FFD700',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: '#fff',
          '&.Mui-selected': {
            backgroundColor: '#FFD700',
            color: '#000',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'black',
        },
      },
    },
  },
});

function UploadForm({ open, handleClose }) {
  const [quality, setQuality] = useState('');
  const [size, setSize] = useState('');
  const [tags, setTags] = useState(''); // THCA or CBD as part of the tags
  const [formData, setFormData] = useState({
    strain: '',
    price: ''
  });
  const [files, setFiles] = useState([]); // Array to hold additional content files
  const [thumbnail, setThumbnail] = useState(null); // Store the selected thumbnail file
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0); // Track progress
  const [isUploading, setIsUploading] = useState(false); // Track if upload is in progress

  // Handle quality selection (single select)
  const handleQualityChange = (event, newQuality) => {
    setQuality(newQuality);
  };

  // Handle size selection (single select)
  const handleSizeChange = (event, newSize) => {
    setSize(newSize);
  };

  // Handle THCA or CBD selection as tags
  const handleTagsChange = (event, newTags) => {
    setTags(newTags);
  };

  // Handle input fields for strain and price
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle thumbnail selection
  const handleThumbnailChange = (e) => {
    setThumbnail(e.target.files[0]); // Store the single thumbnail file
  };

  // Handle additional files selection
  const handleFileChange = (e) => {
    setFiles([...e.target.files]); // Store all selected files
  };

  // Handle the form submission and upload
  const handleUpload = () => {
    const token = localStorage.getItem('token'); // Get token from localStorage

    if (!token) {
      setUploadMessage('You need to be logged in to upload files.');
      return;
    }

    if (!thumbnail || files.length === 0) {
      setUploadMessage('Please select a thumbnail and at least one additional file.');
      return;
    }

    const formDataToUpload = new FormData();
    formDataToUpload.append('strain', formData.strain);
    formDataToUpload.append('price', formData.price);
    formDataToUpload.append('quality', quality);
    formDataToUpload.append('size', size);
    formDataToUpload.append('tags', tags); // Add tags (THCA or CBD)
    formDataToUpload.append('thumbnail', thumbnail); // Upload the thumbnail separately

    // Append additional files
    files.forEach((file) => {
      formDataToUpload.append('files', file); // Appending as 'files'
    });

    // Create XMLHttpRequest for progress tracking
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiUrl}/upload-photo`, true);

    // Set the Authorization header with the token
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    // Disable the form during the upload
    setIsUploading(true);

    // Track progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percentComplete); // Update progress state
      }
    };

    // Handle the response
    xhr.onload = () => {
      if (xhr.status === 200) {
        const result = JSON.parse(xhr.responseText);
        setUploadMessage('Photos and videos uploaded successfully and processed!');

        // Reset the form fields and states
        setFormData({
          strain: '',
          price: ''
        });
        setQuality('');
        setSize('');
        setTags(''); // Reset tags selection
        setThumbnail(null); // Reset thumbnail selection
        setFiles([]); // Reset additional files selection
        setUploadProgress(0); // Reset progress after upload
        setIsUploading(false); // Re-enable form after upload
      } else if (xhr.status === 401) {
        setUploadMessage('Unauthorized: Please log in to upload files.');
        setIsUploading(false); // Re-enable form after upload failure
      } else {
        console.error('Upload failed with status:', xhr.status, 'Response:', xhr.responseText);
        setUploadMessage('Failed to upload the files.');
        setIsUploading(false); // Re-enable form after upload failure
      }
    };

    // Handle errors
    xhr.onerror = () => {
      console.error('Error occurred during upload:', xhr.statusText, 'Response:', xhr.responseText);
      setUploadMessage('Error uploading the files.');
      setIsUploading(false); // Re-enable form after error
    };

    // Send the form data
    xhr.send(formDataToUpload);
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ backgroundColor: 'rgba(0,0,0,.85)', color: 'rgba(244,205,24,1)', position: 'relative' }}>

          {/* Show overlay during upload */}
          {isUploading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 10,
                color: '#fff',
                fontSize: '48px',
              }}
            >
              {uploadProgress}%
            </Box>
          )}

          {/* The rest of the form remains underneath the overlay */}
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Button variant="outlined" component="label" fullWidth disabled={isUploading} sx={{ color: 'rgba(244,205,24,1)', marginRight: '10px' }}>
              Thumbnail
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleThumbnailChange}
                disabled={isUploading} // Disable file input during upload
              />
            </Button>
            <Button variant="outlined" component="label" fullWidth disabled={isUploading} sx={{ color: 'rgba(244,205,24,1)' }}>
              Additional Content
              <input
                type="file"
                hidden
                accept="image/*,video/*"
                multiple
                onChange={handleFileChange}
                disabled={isUploading} // Disable file input during upload
              />
            </Button>
          </Box>

          <TextField
            label="Strain"
            name="strain"
            fullWidth
            margin="normal"
            value={formData.strain}
            onChange={handleInputChange}
            disabled={isUploading}
          />
          <TextField
            label="Price"
            name="price"
            fullWidth
            margin="normal"
            type="number" // This ensures the input only allows numeric values
            inputProps={{ min: 0 }} // Optional: Prevents negative numbers
            value={formData.price}
            onChange={handleInputChange}
            disabled={isUploading}
          />

          {/* Quality Section */}
          <Typography variant="subtitle1" gutterBottom>
            Quality:
          </Typography>
          <ToggleButtonGroup
            value={quality}
            exclusive
            onChange={handleQualityChange}
            aria-label="quality selection"
            disabled={isUploading}
          >
            <ToggleButton value="Exotic" sx={{ backgroundColor: 'rgba(244,205,24,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(244,205,24,1)' }, '&:hover': { backgroundColor: 'rgba(244,205,24,0.7)' } }}>
              Exotic
            </ToggleButton>
            <ToggleButton value="Indoor" sx={{ backgroundColor: 'rgba(244,205,24,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(244,205,24,1)' }, '&:hover': { backgroundColor: 'rgba(244,205,24,0.7)' } }}>
              Indoor
            </ToggleButton>
            <ToggleButton value="Greenhouse" sx={{ backgroundColor: 'rgba(244,205,24,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(244,205,24,1)' }, '&:hover': { backgroundColor: 'rgba(244,205,24,0.7)' } }}>
              GH
            </ToggleButton>
            <ToggleButton value="Outdoor" sx={{ backgroundColor: 'rgba(244,205,24,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(244,205,24,1)' }, '&:hover': { backgroundColor: 'rgba(244,205,24,0.7)' } }}>
              Outdoor
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Size Section */}
          <Typography variant="subtitle1" gutterBottom>
            Size:
          </Typography>
          <ToggleButtonGroup
            value={size}
            exclusive
            onChange={handleSizeChange}
            aria-label="size selection"
            disabled={isUploading}
          >
            <ToggleButton value="Bigs" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              Bigs
            </ToggleButton>
            <ToggleButton value="Mids" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              Mids
            </ToggleButton>
            <ToggleButton value="Mids/Smalls" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              Mids/Smalls
            </ToggleButton>
            <ToggleButton value="Smalls" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              Smalls
            </ToggleButton>
          </ToggleButtonGroup>

          {/* THCA or CBD Section */}
          <Typography variant="subtitle1" gutterBottom>
            Type:
          </Typography>
          <ToggleButtonGroup
            value={tags}
            exclusive
            onChange={handleTagsChange}
            aria-label="THCA or CBD selection"
            disabled={isUploading}
          >
            <ToggleButton value="THCA" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              THCA
            </ToggleButton>
            <ToggleButton value="CBD" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              CBD
            </ToggleButton>
            <ToggleButton value="CBG" sx={{ backgroundColor: 'rgba(127,127,127,0.5)', color: '#fff', '&.Mui-selected': { backgroundColor: 'rgba(127,127,127,1)' } }}>
              CBG
            </ToggleButton>
          </ToggleButtonGroup>

          <Box mt={3}>
            <Button variant="contained" color="primary" fullWidth onClick={handleUpload} disabled={isUploading}>
              Submit
            </Button>
          </Box>

          {uploadMessage && <p>{uploadMessage}</p>}
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}

export default UploadForm;
